import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AdminApi } from '../admin.api'
import { IStoreInterface } from '../../configs/store.config'
import { LoggedUser, Vehicle } from '../../shared/shared.models'
import { RoleEnum, AvailabilityEnum } from '../../shared/shared.enums'
import { PageTitle, Row } from '../../shared/shared.styled'
import { Table } from '../../shared/components/table/table.component'
import { TableUtils } from '../table.utils'
import { SoldVehiclesAnalytics } from '../components/sold-vehicle-analytics.component'
import { useFilter } from '../../shared/hooks/use-filter.hook'
import { SoldVehiclesSearch } from '../components/sold-vehicles-search.component'
import { useNavigate } from 'react-router-dom'
import { VehicleRoutesEnum } from '../../routes.constants'
import { SortOrderEnum, SortUtils } from '../../shared/sort.utils'
import { createSelector } from 'reselect'
import moment from 'moment'
import { VehicleFilters } from '../components/vehicle-search.component'
import { useAlert } from 'react-alert'
import { useHasVehicles } from '../../shared/hooks/use-vehicles-exists.hook'
import { useHasStandsOnly } from '../../shared/hooks/use-just-stand.hook'
import { AdminActions } from '../admin.store'
import PdfGeneratorButton from '../../shared/components/button2.component'
import { enqueueCompanySales } from '../../api/celeryClient'
import useArtifactDownload from '../../hooks/useArtifactDownload'
import { useIsAdmin } from '../../shared/hooks/use-is-admin.hook'

const userSelector = createSelector(
    (state: IStoreInterface) => state.adminReducer.user,
    (user) => user as LoggedUser
)

export const SoldVehiclesPage = () => {
    // Hooks initialization
    const {
        filters,
        updateFilter,
        updateMultipleFilters,
        clearAllFilters,
        clearFilter,
    } = useFilter()

    const company = useSelector(
        (state: IStoreInterface) => state.adminReducer.company
    )

    const navigate = useNavigate()
    const alert = useAlert()
    const isAdmin = useIsAdmin()

    // Redux state
    const loggedUser = useSelector(userSelector)
    const standExistOnly = useHasStandsOnly()
    const vehiclesExists = useHasVehicles()

    const dispatch = useDispatch()

    const { startTask: startSalesCSVTask } = useArtifactDownload({
        getFilename: () => `Lista de Vendas - ${company ? company.name : ''}`,
        successMessage: 'O CSV será transferido automaticamente',
        failureMessage: 'Erro ao gerar o CSV. Por favor tente mais tarde',
        defaultContentType: 'text/csv',
    })

    // Helper function to compare filters
    const areFiltersEqual = React.useCallback(
        (filters1: any, filters2: any) => {
            const keys1 = Object.keys(filters1)
            const keys2 = Object.keys(filters2)

            if (keys1.length !== keys2.length) {
                return false
            }

            return keys1.every((key) => filters1[key] === filters2[key])
        },
        []
    )

    // Local state
    const [vehicles, setVehicles] = React.useState<Vehicle[]>([])
    const [isInitialLoad, setIsInitialLoad] = React.useState(true)
    const [isFetchingVehicles, setIsFetchingVehicles] = React.useState(false)

    // Use ref to track previous filters to prevent duplicate requests with same filters
    const previousFilters = React.useRef<any>({})

    // Function to fetch vehicles with filters
    const fetchVehiclesWithFilters = React.useCallback(
        async (appliedFilters: any) => {
            setIsFetchingVehicles(true)
            try {
                let queryParams = '?availability=SOLD,DELIVERED'
                if (
                    loggedUser.role === RoleEnum.SELLER ||
                    loggedUser.role === RoleEnum.EDITOR1 ||
                    loggedUser.role === RoleEnum.EDITOR2
                ) {
                    queryParams += `&seller_uuid=${loggedUser.uuid}`
                }

                // Add filter parameters
                if (appliedFilters && Object.keys(appliedFilters).length > 0) {
                    Object.keys(appliedFilters).forEach((key) => {
                        if (
                            appliedFilters[key] !== undefined &&
                            appliedFilters[key] !== ''
                        ) {
                            queryParams += `&${key.toLowerCase()}=${
                                appliedFilters[key]
                            }`
                        }
                    })
                }

                const res = await AdminApi.methods.getVehiclesCompany(
                    loggedUser.companyUuid,
                    queryParams
                )
                setVehicles(res.data.vehicles)
                dispatch(
                    AdminActions.methods.getVehiclesSuccessAction(
                        res.data.vehicles
                    )
                )
            } catch (error) {
                alert.error(
                    'Erro ao buscar os veículos. Por favor tente mais tarde'
                )
            } finally {
                setIsFetchingVehicles(false)
            }
        },
        [
            alert,
            dispatch,
            loggedUser.companyUuid,
            loggedUser.role,
            loggedUser.uuid,
        ]
    )

    // Effect to set default filters and fetch initial vehicles
    React.useEffect(() => {
        const queryParams = new URLSearchParams(window.location.search)
        if (queryParams.toString() === '') {
            // No URL parameters - set default month/year filters and fetch vehicles
            const todaysDate = moment()
            const currentMonth = todaysDate.month() + 1 // Months are zero based
            const currentYear = todaysDate.year()
            const defaultFilters = {
                [VehicleFilters.SALE_YEAR]: currentYear,
                [VehicleFilters.SALE_MONTH]: currentMonth,
            }
            updateMultipleFilters([
                {
                    key: VehicleFilters.SALE_YEAR,
                    value: currentYear,
                },
                {
                    key: VehicleFilters.SALE_MONTH,
                    value: currentMonth,
                },
            ])
            fetchVehiclesWithFilters(defaultFilters)
            previousFilters.current = defaultFilters
        } else {
            // URL parameters exist - parse them and fetch vehicles with those filters
            const urlFilters: any = {}
            queryParams.forEach((value, key) => {
                // Convert URL parameter keys to VehicleFilters enum format
                const filterKey = key.toUpperCase() as VehicleFilters
                if (Object.values(VehicleFilters).includes(filterKey)) {
                    urlFilters[filterKey] = value
                    // Also update the filter state so the UI shows the correct values
                    updateFilter(filterKey, value)
                }
            })
            fetchVehiclesWithFilters(urlFilters)
            previousFilters.current = urlFilters
        }
        setIsInitialLoad(false)
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    // Effect to fetch vehicles when filters change
    React.useEffect(() => {
        // Skip the initial load, but handle all subsequent filter changes
        if (!isInitialLoad) {
            // Only fetch if filters have actually changed
            if (!areFiltersEqual(filters, previousFilters.current)) {
                fetchVehiclesWithFilters(filters)
                previousFilters.current = { ...filters }
            }
        }
    }, [filters, fetchVehiclesWithFilters, isInitialLoad, areFiltersEqual])

    React.useEffect(() => {
        AdminApi.methods
            .getFinancials(loggedUser.companyUuid)
            .then((res) => {
                dispatch(
                    AdminActions.methods.getFinancialsSuccessAction(
                        res.data.financials
                    )
                )
            })
            .catch((e) => {
                alert.error(
                    'Houve um erro a ir buscar a lista de financeiras, por favor tente mais tarde'
                )
            })
    }, [alert, dispatch, loggedUser.companyUuid])

    // const goToVehicleDetailsPage = (vehicle: Vehicle) => {
    //   navigate(RoutesEnum.VEHICLE_PAGE.replace(":vehicleId", vehicle.uuid));
    // };

    const goToVehicleSalePage = (vehicle: Vehicle) => {
        navigate(
            VehicleRoutesEnum.SALE_DETAILS.replace(':vehicleId', vehicle.uuid)
        )
    }

    const onHeaderClick = (header: string, order: SortOrderEnum) => {
        const sortedVehicles = SortUtils.sortVehicles(vehicles, header, order)
        setVehicles(sortedVehicles)
    }

    const enqueueCSVSales = async (
        companyUuid: string,
        companyName: string
    ) => {
        try {
            const res = await enqueueCompanySales(companyUuid)
            startSalesCSVTask(res.task_id)
        } catch (err) {
            console.error(err)
            alert.error('Erro ao enfileirar o CSV de vendas')
        }
    }

    return (
        <>
            <Row style={{ justifyContent: 'space-between' }}>
                <PageTitle>Veículos vendidos</PageTitle>
                {isAdmin && (
                    <PdfGeneratorButton
                        icon={'/icons/csv.png'}
                        action={() =>
                            enqueueCSVSales(
                                company ? company.uuid : '',
                                company ? company.name : ''
                            )
                        }
                        title={''}
                    />
                )}
            </Row>
            <SoldVehiclesSearch
                updateFilter={updateFilter}
                filters={filters}
                clearAllFilters={clearAllFilters}
                clearFilter={clearFilter}
            />
            <SoldVehiclesAnalytics visibleVehicles={vehicles} />
            <Table<Vehicle>
                isLoadingData={isFetchingVehicles}
                rowStyles={(value) =>
                    value.availability === AvailabilityEnum.SOLD
                        ? { backgroundColor: '#fcdede' }
                        : {}
                }
                values={vehicles}
                columns={TableUtils.getSoldVehiclesColumns(
                    loggedUser,
                    onHeaderClick
                )}
                onRowClick={goToVehicleSalePage}
                standExistOnly={standExistOnly}
                vehiclesExists={vehiclesExists}
                getUrl={(value) =>
                    VehicleRoutesEnum.SALE_DETAILS.replace(
                        ':vehicleId',
                        value.uuid
                    )
                }
            />
        </>
    )
}
